import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FaArrowLeft, FaChartBar, FaDownload, FaTable } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const REPORT_TYPES = [
  { value: 'progress', label: 'Learner Progress' },
  { value: 'completion', label: 'Journey Completion' },
  { value: 'assessment', label: 'Assessment Results' },
  { value: 'vilt_attendance', label: 'VILT Attendance' },
  { value: 'time_spent', label: 'Time Spent' },
  { value: 'certificates', label: 'Certificates' }
];

const LearningJourneyReports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportType, setReportType] = useState('progress');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.learningJourneys.getReport(id, reportType);
      setData(res.data?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [id, reportType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async (format) => {
    try {
      const res = await api.learningJourneys.exportReport(id, reportType, format);
      if (format === 'pdf') {
        const blob = new Blob([JSON.stringify(res.data?.data || res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `journey_${id}_${reportType}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.info('PDF source exported. Use print/PDF from browser for formatted PDF.');
        window.print();
        return;
      }
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journey_${id}_${reportType}.${format === 'excel' ? 'csv' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Export failed');
    }
  };

  const items = data?.items || [];
  const columns = items.length > 0
    ? Object.keys(items[0]).filter((k) => k !== 'step_details')
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3 md:p-6 lg:p-8">
      <div className="mb-4">
        <Row className="align-items-center g-3">
          <Col md={2}>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/admin/learning-journeys')}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              aria-label="Back to learning journeys"
            >
              <FaArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </Button>
          </Col>
          <Col md={10}>
            <h5 className="m-0 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 md:text-3xl">
              Journey Reports
            </h5>
            <p className="mb-0 mt-1 text-sm text-gray-600">{data?.journey?.name || 'Loading...'}</p>
          </Col>
        </Row>
      </div>

      <Row className="mb-4 g-3">
        <Col xs={12}>
          <div className="flex w-full flex-wrap items-center gap-2 rounded-xl border border-purple-100 bg-white p-2 shadow-lg">
          <Form.Select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="min-w-48 flex-grow !border-0 !bg-transparent lg:flex-grow-0"
            aria-label="Report type"
          >
            {REPORT_TYPES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </Form.Select>
          <Button bsPrefix="custom-hamburger" className="rounded-lg border border-green-400 bg-white px-3 py-2 text-sm font-semibold text-green-600 hover:bg-green-50" onClick={() => handleExport('csv')} aria-label="Export CSV">
            <FaDownload className="me-1" aria-hidden="true" /> CSV
          </Button>
          <Button bsPrefix="custom-hamburger" className="rounded-lg border border-blue-400 bg-white px-3 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50" onClick={() => handleExport('excel')} aria-label="Export Excel">
            <FaDownload className="me-1" aria-hidden="true" /> Excel
          </Button>
          <Button bsPrefix="custom-hamburger" className="rounded-lg border border-red-400 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50" onClick={() => handleExport('pdf')} aria-label="Export PDF">
            <FaDownload className="me-1" aria-hidden="true" /> PDF
          </Button>
          </div>
        </Col>
      </Row>

      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {Object.entries(data.summary).map(([key, value]) => (
            <div key={key} className="rounded-2xl border border-purple-100 bg-white/90 p-4 text-center shadow-sm">
              <div className="text-xs uppercase text-gray-500 tracking-wide">{key.replace(/_/g, ' ')}</div>
              <div className="text-2xl font-bold text-purple-700">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-purple-100 bg-white/90 shadow-2xl">
        {loading ? (
          <div className="text-center py-12"><Spinner animation="border" /></div>
        ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-16 text-center text-slate-500">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50 text-2xl text-purple-500">
                <FaChartBar aria-hidden="true" />
              </span>
              <h3 className="mb-1 text-lg font-bold text-slate-800">No report data</h3>
              <p className="mb-0 text-sm">Data will appear here when learners begin this journey.</p>
            </div>
        ) : (
            <Table bordered responsive hover className="m-0 min-w-max align-middle sm:min-w-0">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  {columns.map((col) => (
                    <th key={col} className="!bg-purple-600 !px-3 !py-3 !text-white">
                      <span className="flex items-center gap-2 capitalize">
                        <FaTable className="h-3.5 w-3.5" aria-hidden="true" />
                        {col.replace(/_/g, ' ')}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((col) => (
                      <td key={col} className="px-3">
                        {typeof row[col] === 'boolean' ? (row[col] ? 'Yes' : 'No') : String(row[col] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
        )}
      </div>
    </div>
  );
};

export default LearningJourneyReports;
